<template>
  <EditingDialog
    :useDifferentShowDialogProp="true"
    :useDifferentSaveFunction="true"
    :differentSaveFunction="saveConditionsSplits"
    :additionalCloseFunction="clearConditionsSplitDialogForm"
    maxDialogWidth="1280"
    :context="context + 'ConditionsSplits'"
    :showSnackbar="true"
    snackbarTextLangConst="lang_GENERAL_SNACKBAR_TEXT_CLICK_MAIN_SAVE_BUTTON"
  >
    <template v-slot:editForm>
      <v-container fluid>
        <v-row>
          <v-col cols="6">
            <BaseEditPageDropdown
              :context="context + 'ConditionsSplits'"
              field="RightsType"
              :items="rightsDropdownItems"
              :label="
                $t('lang_GENERIC_CAPTION_RIGHTS_TYPE')
              "
              :selection="true"
              rules="required"
              :withMargin="false"
              :clearable="false"
              :alvqa="alvqa + '-dropdown-rights-type'"
            />
          </v-col>
          <v-col cols="6"></v-col>
          <v-col cols="6">
            <BaseEditPageTextField
              :context="context + 'ConditionsSplits'"
              field="Category"
              :label="$t('lang_GENERIC_CAPTION_CATEGORY')"
              :withMargin="false"
              :alvqa="alvqa + '-textfield-category'"
            />
          </v-col>
          <v-col cols="6">
            <BaseEditPageTextField
              :context="context + 'ConditionsSplits'"
              field="SubCategory"
              :withMargin="false"
              :label="$t('lang_PUBLISHING_INCOME_CAPTION_SUB_CATEGORY')"
              :alvqa="alvqa + '-textfield-subcategory'"
            />
          </v-col>
          <v-col cols="12">
            <!-- Mixed view - show bulk OR normal for each field independently -->
            <v-row>
              <!-- Include Region Column -->
              <v-col cols="5">
                <BaseEditPageAutocomplete
                  v-if="!showBulkInclude"
                  :context="context + 'ConditionsSplits'"
                  field="Include"
                  :label="$t('lang_PUBLISHING_WORKS_EXPORT_INCLUDE_TERRITORY')"
                  :withMargin="false"
                  :multiple="true"
                  :withFreeText="false"
                  :isLoading="true"
                  :returnObject="false"
                  :alvqa="alvqa + '-dropdown-regions-include'"
                >
                  <template v-slot:selection></template>
                  <template v-slot:items="{ items }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ items.text }} ({{ items.value }})
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </BaseEditPageAutocomplete>
                <v-text-field
                  v-else
                  v-model="bulkIncludeText"
                  :label="$t('lang_GENERIC_CAPTION_REGION_INCLUDE_BULK')"
                  :placeholder="$t('lang_GENERIC_CAPTION_REGION_BULK_PLACEHOLDER')"
                  outlined
                  dense
                  :withMargin="false"
                  :alvqa="alvqa + '-bulk-include-input'"
                  @keyup.enter="parseBulkInclude"
                  class="bulk-entry-field"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      small
                      color="primary"
                      @click="parseBulkInclude"
                      :disabled="!bulkIncludeText || bulkIncludeText.trim() === ''"
                      :alvqa="alvqa + '-bulk-include-add-btn'"
                      class="ml-2 bulk-add-btn"
                    >
                      +
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <!-- Include Toggle Button - Always visible -->
              <v-col cols="1" class="d-flex align-center">
                <span>
                  <button 
                    type="button" 
                    class="v-btn v-btn--icon v-btn--round v-btn--text theme--light v-size--default buttonStandard--text"
                    @click="showBulkInclude = !showBulkInclude"
                  >
                    <span class="v-btn__content">
                      <i 
                        aria-hidden="true" 
                        :alvqa="alvqa + '-toggle-bulk-include-paste-icon'" 
                        class="v-icon notranslate material-icons theme--light" 
                        style="font-size: 16px;"
                      >
                        content_paste
                      </i>
                    </span>
                  </button>
                </span>
              </v-col>
              <!-- Exclude Region Column -->
              <v-col cols="5">
                                <BaseEditPageAutocomplete
                  v-if="!showBulkExclude"
                  :context="context + 'ConditionsSplits'"
                  field="Exclude"
                  :label="$t('lang_PUBLISHING_WORKS_EXPORT_EXCLUDE_TERRITORY')"
                  :withMargin="false"
                  :multiple="true"
                  :withFreeText="false"
                  :isLoading="true"
                  :returnObject="false"
                  :disabled="!(includedRegions.includes('WORLD') || includedRegions.includes('WELT'))"
                  :alvqa="alvqa + '-dropdown-regions-exclude'"
                >
                  <template v-slot:selection></template>
                  <template v-slot:items="{ items }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ items.text }} ({{ items.value }})
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </BaseEditPageAutocomplete>
                <v-text-field
                  v-else
                  v-model="bulkExcludeText"
                  :label="$t('lang_GENERIC_CAPTION_REGION_EXCLUDE_BULK')"
                  :placeholder="$t('lang_GENERIC_CAPTION_REGION_BULK_PLACEHOLDER')"
                  outlined
                  dense
                  :withMargin="false"
                  :alvqa="alvqa + '-bulk-exclude-input'"
                  @keyup.enter="parseBulkExclude"
                  class="bulk-entry-field"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      small
                      color="primary"
                      @click="parseBulkExclude"
                      :disabled="!bulkExcludeText || bulkExcludeText.trim() === ''"
                      :alvqa="alvqa + '-bulk-exclude-add-btn'"
                      class="ml-2"
                    >
                      +
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <!-- Exclude Toggle Button - Always visible -->
              <v-col cols="1" class="d-flex align-center">
                <span>
                  <button 
                    type="button" 
                    class="v-btn v-btn--icon v-btn--round v-btn--text theme--light v-size--default buttonStandard--text"
                    @click="showBulkExclude = !showBulkExclude"
                  >
                    <span class="v-btn__content">
                      <i 
                        aria-hidden="true" 
                        :alvqa="alvqa + '-toggle-bulk-exclude-paste-icon'" 
                        class="v-icon notranslate material-icons theme--light" 
                        style="font-size: 16px;"
                      >
                        content_paste
                      </i>
                    </span>
                  </button>
                </span>
              </v-col>
            </v-row>
            
            <!-- Selected Regions Display (chips) -->
            <v-row v-if="showSelectedRegions()" class="mt-0">
              <v-col cols="6" class="mt-0">
                <span v-for="(region, index) in includedRegions" :key="index">
                  <v-chip 
                    color="iconStandard" 
                    close
                    text-color="white"
                    @click:close="removeIncluded(region, index)"
                    class="mr-2"
                  >
                    {{ getRegionDisplayName(region) }}
                  </v-chip>
                </span>
              </v-col>
              <v-col cols="6">
                <span v-for="(region, index) in excludedRegions" :key="index">
                  <v-chip 
                    color="iconStandard" 
                    close
                    text-color="white"
                    @click:close="removeExcluded(region, index)"
                    class="mr-2"
                  >
                    {{ getRegionDisplayName(region) }}
                  </v-chip>
                </span>
              </v-col>
            </v-row>

          </v-col>

          <v-col cols="12" class="calculation-parameters">
            <v-row>
              <v-col cols="3">
                <BaseEditPageDropdown
                  :context="context + 'ConditionsSplits'"
                  field="ApplyTo"
                  :items="applyToDropdownItems"
                  :label="
                    $t(
                      'lang_CONTRACTS_PUBLISHING_CAPTION_CONDITIONS_SPLITS_APPLY_TO'
                    )
                  "
                  :selection="true"
                  :withMargin="false"
                  hideDetails
                  rules="required"
                  :clearable="false"
                  :alvqa="alvqa + '-dropdown-apply-to'"
                />
              </v-col>
              <v-col cols="5" align-self="end" class="text-right">
                <template>
                  <BaseEditPageAutocomplete
                    :context="context + 'ConditionsSplits'"
                    field="RecipientId"
                    :label="conditionRecipient"
                    :rules="isApplyToIncomingMoney ? '' : 'required'"
                    :withMargin="false"
                    :withFreeText="false"
                    :disabled="isApplyToIncomingMoney"
                    :alvqa="
                      alvqa +
                      '-recipient-textfield-autocomplete'
                    "
                  >
                    <template v-slot:selection="{ item }">
                      <div
                        class="ellipsis"
                        :alvqa="
                          alvqa +
                          '-autocomplete-conditions-splits-recipient-selection'
                        "
                      >
                        {{ item.gemaName }}
                        <span v-if="$alvPlugin.printName(item)">
                          ({{ item.firstName }} {{ item.lastName }})
                        </span>
                      </div>
                    </template>
                    <template v-slot:items="{ items }">
                      <v-list-item-content
                        :alvqa="
                          alvqa +
                          '-autocomplete-conditions-splits-recipient-dropdown-' +
                          items.alvId
                        "
                      >
                        <v-list-item-title
                          :alvqa="
                            alvqa +
                            '-autocomplete-conditions-splits-recipient-dropdown-' +
                            items.alvId +
                            '-title'
                          "
                        >
                          {{ items.gemaName }}
                          {{
                            $alvPlugin.printName(items)
                              ? "(" + items.firstName + " " + items.lastName + ")"
                              : ""
                          }}
                          <!--  
                                      typeOfAz:
                                          allowed values: P, D or G
                                          P => Person => P in circle
                                          D => Distributor => D in circle (in german V for 'Vertrieb')
                                          G => Group => G in circle                                                
                                      -->
                          <v-avatar color="grey" size="20" class="caption">{{
                            items.typeOfAz
                          }}</v-avatar>
                          <!-- TODO: check i18n {{ i18n.locale === 'de' && items.item.typeOfAz === 'D' ? 'V' : items.item.typeOfAz }} -->
                        </v-list-item-title>
                        <v-list-item-subtitle
                          :alvqa="
                            alvqa +
                            '-autocomplete-conditions-splits-recipient-dropdown-' +
                            items.alvId +
                            '-subtitle'
                          "
                        >
                          {{ $t("lang_IPI") }}:
                          {{
                            items.ipi !== null &&
                            items.ipi !== "" &&
                            items.ipi !== undefined
                              ? items.ipi
                              : $t("lang_GENERIC_CAPTION_NOMEN_NOMINANDUM")
                          }}
                          | {{ items.alvId }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </BaseEditPageAutocomplete>
                </template>
              </v-col>
              <v-col cols="4">
                <BaseEditPageDropdown
                  :context="context + 'ConditionsSplits'"
                  field="Order"
                  :items="orderDropdownItems"
                  :label="
                    $t(
                      'lang_CONTRACTS_PUBLISHING_CAPTION_CONDITIONS_SPLITS_ORDER'
                    )
                  "
                  :selection="true"
                  :withMargin="false"
                  hideDetails
                  :clearable="true"
                  :disabled="!isApplyToIncomingMoney"
                  :alvqa="alvqa + '-dropdown-order'"
                />
              </v-col>
              <v-col cols="6">
                <BaseEditPageTextField
                  :context="context + 'ConditionsSplits'"
                  field="Split"
                  :label="$t('lang_GENERAL_CAPTION_SPLIT')"
                  :withMargin="false"
                  rules="percentDecimal|required"
                  :alvqa="alvqa + '-textfield-split'"
                  suffix="%"
                />
              </v-col>
              <v-col cols="6">
                <BaseEditPageTextField
                  :context="context + 'ConditionsSplits'"
                  field="Base"
                  :label="
                    $t('lang_CONTRACTS_PUBLISHING_EDIT_SHARES_PANEL_CAPTION_BASE')
                  "
                  :withMargin="false"
                  rules="percentDecimal|required"
                  :alvqa="alvqa + '-textfield-base'"
                  suffix="%"
                />
              </v-col>
              <v-col cols="5">
                <BaseEditPageDropdown
                  :context="context + 'ConditionsSplits'"
                  field="Weighting"
                  :items="weightingItems"
                  :label="$t('lang_PUBLISHING_CONTRACTS_WEIGHTING_BY')"
                  :selection="true"
                  :withMargin="false"
                  hideDetails
                  :clearable="false"
                  :additionalFunction="updateOnWeightingChange"
                  :alvqa="alvqa + '-dropdown-weighting'"
                />
              </v-col>
              <v-col cols="3">
                <BaseEditPageDropdown
                  :context="context + 'ConditionsSplits'"
                  field="WeightingSource"
                  :items="weightingSourceItems"
                  :label="$t('lang_PUBLISHING_CAPTION_SHARE_TYPE')"
                  :selection="true"
                  :withMargin="false"
                  hideDetails
                  :clearable="false"
                  :disabled="areWeightingSharesDisabled"
                  :rules="areWeightingSharesDisabled ? '' : 'required'"
                  :alvqa="alvqa + '-dropdown-weighting-source'"
                />
              </v-col>
              <v-col cols="4">
                <BaseEditPageDropdown
                  v-if="!isAuthorSpecificWeighting"
                  :context="context + 'ConditionsSplits'"
                  field="SharesToConsider"
                  :items="sharesToConsiderItems"
                  :label="$t('lang_CONTRACTS_PUBLISHING_CAPTION_SHARES_TO_CONSIDER')"
                  :selection="true"
                  :withMargin="false"
                  hideDetails
                  :clearable="false"
                  :disabled="areWeightingSharesDisabled"
                  :rules="areWeightingSharesDisabled ? '' : 'required'"
                  :alvqa="alvqa + '-dropdown-shares-to-consider'"
                />
                <BaseEditPageAutocomplete
                  v-else
                  :context="context + 'ConditionsSplits'"
                  field="SharesToConsider"
                  :label="$t('lang_CONTRACTS_PUBLISHING_CAPTION_SHARES_TO_CONSIDER')"
                  :withMargin="false"
                  :withFreeText="false"
                  :disabled="areWeightingSharesDisabled"
                  :rules="areWeightingSharesDisabled ? '' : 'required'"
                  hideDetails
                  :additionalFunction="cacheSelectedAzPerson"
                  :alvqa="alvqa + '-autocomplete-shares-to-consider-author'"
                >
                  <template v-slot:selection="{ item }">
                    <div
                      class="ellipsis"
                      :alvqa="alvqa + '-autocomplete-shares-to-consider-author-selection'"
                    >
                      {{ $alvPlugin.showNameOfAzEntry(item) }}
                    </div>
                  </template>
                  <template v-slot:items="{ items }">
                    <v-list-item-content
                      :alvqa="alvqa + '-autocomplete-shares-to-consider-author-dropdown-' + items.alvId"
                    >
                      <v-list-item-title
                        :alvqa="alvqa + '-autocomplete-shares-to-consider-author-dropdown-' + items.alvId + '-title'"
                      >
                        {{ $alvPlugin.showNameOfAzEntry(items) }}
                        <v-avatar color="grey" size="20" class="caption">{{
                          items.typeOfAz
                        }}</v-avatar>
                      </v-list-item-title>
                      <v-list-item-subtitle
                        :alvqa="alvqa + '-autocomplete-shares-to-consider-author-dropdown-' + items.alvId + '-subtitle'"
                      >
                        {{ $t("lang_IPI") }}: {{ items.ipi !== '' ? items.ipi : $t('lang_GENERIC_CAPTION_NONE') }} | {{ items.alvId }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </BaseEditPageAutocomplete>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </EditingDialog>
</template>

<script>
import BaseEditPageAutocomplete from "@/components/_editForm/BaseEditPageAutocomplete";
import EditingDialog from "@/components/_dialogs/EditingDialog";
import BaseEditPageDropdown from "@/components/_editForm/BaseEditPageDropdown";
import BaseEditPageTextField from "@/components/_editForm/BaseEditPageTextField";
import BaseRegions from "@/components/_base/BaseRegions.vue"
import { parseBulkCountryString, splitTerritoryCodes } from "@/utils/territoryBulkPaste"

import {
  DropdownContractsPublishingSplitsApplyTo,
  DropdownRightsType,
  DropdownCategory,
  DropdownWeighting,
	DropdownWeightingSource,
  DropdownSharesToConsider,
  DropdownContractsPublishingSplitsOrder,
} from "@/assets/dropdowns/alvDropdowns.js";

export default {
  name: "PublishingContractsSplitsEditDialog",
  components: {
    BaseEditPageTextField,
    BaseEditPageDropdown,
    EditingDialog,
    BaseEditPageAutocomplete,
    BaseRegions
  },
  data() {
    return {
      items: [],
      context: "PublishingContract",
      alvqa: "contracts-publishing-edit-form-splits-dialog",
      bulkIncludeText: '',
      bulkExcludeText: '',
      showBulkInclude: false,
      showBulkExclude: false
    };
  },
  computed: {
    fieldRightsType() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsRightsType;
    },
    fieldApplyTo() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsApplyTo;
    },
    fieldCategory() {
      let value = this.$store.getters.getPublishingContractConditionsSplitsDetailsCategory;
      return value !== '' ? value : null;
    },
    fieldSubCategory() {
      let value = this.$store.getters.getPublishingContractConditionsSplitsDetailsSubCategory;
      return value !== '' ? value : null;
    },
    fieldSharesToConsider() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsSharesToConsider;
    },
    fieldWeighting() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsWeighting;
    },
    fieldWeightingSource() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsWeightingSource;
    },
    fieldSplit() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsSplit;
    },
    fieldBase() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsBase;
    },
    fieldRecipientId() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsRecipientId;
    },
    fieldOrder() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsOrder;
    },
    isApplyToIncomingMoney() {
      const applyTo = this.fieldApplyTo;
      if (applyTo =="incomingMoney") {
        this.$store.commit("setPublishingContractConditionsSplitsDetailsRecipientId", null);
      } else {
        this.$store.commit("setPublishingContractConditionsSplitsDetailsOrder", null);
      }
      return applyTo == 'incomingMoney';
    },
    showApplyToRecipientField() {
      var value = this.fieldApplyTo;
      return value && value == 'byAlvId';
    },
    conditionRecipient() {
      return this.$t("lang_GENERIC_CAPTION_AZ_ENTRY");
    },
    rightsDropdownItems() {
      return DropdownRightsType;
    },
    categoryItems() {
      return DropdownCategory;
    },
    weightingItems() {
      // Use the static dropdown data and translate headers dynamically
      return DropdownWeighting.map(item => {
        if (item.header) {
          // Translate header items
          return {
            header: this.$t(item.header),
            alvqa: this.alvqa + '-weighting-header'
          };
        } else {
          // Translate text items
          return {
            ...item,
            text: this.$t(item.text)
          };
        }
      });
    },
    getSocieties() {
      let data = this.$store.getters.getAzSocietiesWhereDirectMember
      let retArr = []
      let homeSociety = JSON.parse(localStorage.getItem("userData"))['user']['homeSocietyCode']
      let homeSocietyName = this.$alvPlugin.getSocietyPanelHeadline(parseInt(homeSociety))

      retArr.push({
        value: homeSociety,
        text: homeSocietyName,
        alvqa: this.alvqa + `-weighting-source-society-${homeSocietyName}`
      })
      
      // Prepare data for dropdown
      data.forEach((v) => {
          if (parseInt(v.societyCode) != parseInt(homeSociety) && v.societyCode !== null) {
            retArr.push({
                value: v.societyCode,
                text: this.$alvPlugin.getSocietyPanelHeadline(v.societyCode),
                alvqa: this.alvqa + `-weighting-source-society-${v.societyAcronym}`
            })
          }
      })

      return retArr
    },
    weightingSourceItems() {
      let shareTypes = DropdownWeightingSource;
      let societiesOfUser = this.getSocieties
      
			return shareTypes.concat(societiesOfUser);
    },
    sharesToConsiderItems() {
      let items = JSON.parse(JSON.stringify(DropdownSharesToConsider));
      return items;
    },
    applyToDropdownItems() {
      let alvqa = this.alvqa + "-dropdown-apply-to";
      let applyToArr;
      let applyTo = DropdownContractsPublishingSplitsApplyTo;

      if (applyTo !== undefined) {
        applyToArr = JSON.parse(JSON.stringify(applyTo));
        applyToArr.forEach((element) => {
          element["alvqa"] = alvqa + element.value;
        });
      }
      return applyToArr;
    },
    orderDropdownItems() {
      return DropdownContractsPublishingSplitsOrder
    },
    includeItems() {
      let storeIncludes = this.$alvPlugin.getListOfTranslatedCountries();
      storeIncludes.unshift({
        text: this.$t("lang_CAPTION_REGION_WORLD"),
        value: "WORLD",
      });
      return storeIncludes;
    },
    excludeItems() {
      // Filter out WORLD and WELT from exclude options - not allowed
      return this.$alvPlugin.getListOfTranslatedCountries().filter(item => item.value !== 'WORLD' && item.value !== 'WELT');
    },
    areWeightingSharesDisabled() {
      return this.fieldWeighting === null
    },
    isAuthorSpecificWeighting() {
      return ['prSelectedAuthorAllUvz', 'prSelectedAuthorAzUvz', 'mrSelectedAuthorAllUvz', 'mrSelectedAuthorAzUvz'].includes(this.fieldWeighting)
    },
    includedRegions() {
      console.log(this.$store.getters.getPublishingContractConditionsSplitsDetailsInclude)
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsInclude || []
    },
    excludedRegions() {
      return this.$store.getters.getPublishingContractConditionsSplitsDetailsExclude || []
    }
  },
  methods: {
    saveConditionsSplits() {
      var conditionId = this.$store.getters.getActiveConditionId;
      var currentSplitId =
        this.$store.getters.getSelectedPublishingContractConditionsSplits;
      var mappedValues =
        this.$store.getters.getPublishingContractsDetails.conditions.map(
          (el) => {
            if (el.id == conditionId) {
              let region = {
                include: this.$store.getters.getPublishingContractConditionsSplitsDetailsInclude,
                exclude: this.$store.getters.getPublishingContractConditionsSplitsDetailsExclude,
              }
              if (currentSplitId) {
                el.splits = el.splits.map((split) => {
                  if (split.id == currentSplitId) {
                    split.rightsType = this.fieldRightsType;
                    split.category = this.fieldCategory;
                    split.subCategory = this.fieldSubCategory;
                    split.split = this.$alvPlugin.getNumberLocalization(this.fieldSplit, 'number');
                    split.weighting = this.fieldWeighting;
										split.weightingSource = this.fieldWeightingSource;
                    split.sharesToConsider = this.fieldSharesToConsider;
                    split.applyTo = this.fieldApplyTo;
                    split.base =
                     this.$alvPlugin.getNumberLocalization(this.fieldBase, 'number');
                    split.recipientId =
                      this.fieldApplyTo != 'incomingMoney' ? this.fieldRecipientId : null;
                    split.order =
                      this.fieldApplyTo == 'incomingMoney' ? this.fieldOrder : null;
                    split.region = region
                  }
                  return split;
                });
              } else {
                el.splits.push({
                  id: -Math.floor(Math.random() * 10000),
                  rightsType: this.fieldRightsType,
                  category: this.fieldCategory,
                  subCategory: this.fieldSubCategory,
                  split: this.$alvPlugin.getNumberLocalization(this.fieldSplit, 'number'),
                  weighting: this.fieldWeighting,
									weightingSource: this.fieldWeightingSource,
                  sharesToConsider: (() => {
                    return this.isAuthorSpecificWeighting && typeof this.fieldSharesToConsider === 'object' && this.fieldSharesToConsider !== null
                      ? this.fieldSharesToConsider.alvId 
                      : this.fieldSharesToConsider;
                  })(),
                  applyTo: this.fieldApplyTo,
                  base: this.$alvPlugin.getNumberLocalization(this.fieldBase, 'number'),
                  recipientId: this.fieldRecipientId,
                  order: this.fieldOrder,
                  region: region,
                });
              }
            }

            return el;
          }
        );

      var toUpdate = this.$store.getters.getPublishingContractsDetails;
      toUpdate.conditions = mappedValues;
      this.$store.commit("setPublishingContractsDetails", toUpdate);
      this.clearConditionsSplitDialogForm();
    },
    clearConditionsSplitDialogForm() {
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsRightsType",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsCategory",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsSubCategory",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsSplit",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsWeighting",
        null
      );
			this.$store.commit(
				"setPublishingContractConditionsSplitsDetailsWeightingSource",
				null
			);
      this.$store.commit(
				"setPublishingContractConditionsSplitsDetailsSharesToConsider",
				null
			);
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsApplyTo",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsBase",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsRecipientId",
        null
      );
      this.$store.commit(
        "setPublishingContractConditionsSplitsDetailsOrder",
        null
      );
      
      // Clear autocomplete options to prevent stale data
      this.$store.commit(
        "setPublishingContractConditionsSplitsSharesToConsiderOptions",
        []
      );
      
      // Reset bulk entry state
      this.showBulkInclude = false;
      this.showBulkExclude = false;
      this.bulkIncludeText = '';
      this.bulkExcludeText = '';
    },
    updateOnWeightingChange() {
      let weightedBy = this.fieldWeighting;
      if (weightedBy === null) {
        this.$store.commit(
          "setPublishingContractConditionsSplitsDetailsWeightingSource",
          null
        );
        this.$store.commit(
          "setPublishingContractConditionsSplitsDetailsSharesToConsider",
          null
        );
      }
      
      // Clear autocomplete options when weighting type changes to prevent stale data
      this.$store.commit(
        "setPublishingContractConditionsSplitsSharesToConsiderOptions",
        []
      );
    },
    parseBulkInclude() {
      if (!this.bulkIncludeText || this.bulkIncludeText.trim() === '') return;
      
      const parsedCountries = parseBulkCountryString(this.bulkIncludeText);
      const validCountries = this.validateCountryCodes(parsedCountries);
      
      if (validCountries.length > 0) {
        // Get current included regions or initialize as empty array
        const currentIncluded = this.$store.getters.getPublishingContractConditionsSplitsDetailsInclude || [];
        
        // Merge with existing selections (avoid duplicates)
        const mergedCountries = [...new Set([...currentIncluded, ...validCountries])];
        
        // Update store
        this.$store.commit('setPublishingContractConditionsSplitsDetailsInclude', mergedCountries);
        
        // Clear the input and toggle back to autocomplete view
        this.bulkIncludeText = '';
        this.showBulkInclude = false;
      }
    },
    parseBulkExclude() {
      if (!this.bulkExcludeText || this.bulkExcludeText.trim() === '') return;
      
      const parsedCountries = parseBulkCountryString(this.bulkExcludeText);
      const validCountries = this.validateCountryCodes(parsedCountries);
      
      if (validCountries.length > 0) {
        // Get current excluded regions or initialize as empty array
        const currentExcluded = this.$store.getters.getPublishingContractConditionsSplitsDetailsExclude || [];
        
        // Merge with existing selections (avoid duplicates)
        const mergedCountries = [...new Set([...currentExcluded, ...validCountries])];
        
        // Update store
        this.$store.commit('setPublishingContractConditionsSplitsDetailsExclude', mergedCountries);
        
        // Clear the input and toggle back to autocomplete view
        this.bulkExcludeText = '';
        this.showBulkExclude = false;
      }
    },
    validateCountryCodes(countryCodes) {
      // Get list of valid country codes from the available items
      const availableCountries = this.$alvPlugin.getListOfTranslatedCountries();
      const validCodes = availableCountries.map(country => country.value);

      // Add WORLD as valid option
      validCodes.push('WORLD');

      // Silently ignore invalid codes
      return splitTerritoryCodes(countryCodes, validCodes).valid;
    },
    cancelBulkInclude() {
      this.bulkIncludeText = '';
      this.showBulkInclude = false;
    },
    cancelBulkExclude() {
      this.bulkExcludeText = '';
      this.showBulkExclude = false;
    },
    getRegionDisplayName(code) {
      const regionGroups = {
        'WELT': { de: 'Welt',                  en: 'World' },
        'DACH': { de: 'DACH',                   en: 'GAS' },
        'EUR':  { de: 'Europa',                 en: 'Europe' },
        'MEA':  { de: 'Naher Osten & Afrika',   en: 'Middle East & Africa' },
      }
      if (regionGroups[code]) {
        const lang = (this.$i18n.locale === 'de') ? 'de' : 'en'
        return regionGroups[code][lang]
      }
      const translated = this.$alvPlugin.getTranslatedCountryName(code, true)
      return translated || code
    },
    removeIncluded(item, index) {
      if (index >= 0) {
        const currentIncluded = [...this.includedRegions];
        const currentExcluded = this.excludedRegions || [];
        
        // If there are exclude entries, don't allow include to become empty
        if (currentIncluded.length === 1 && currentExcluded.length > 0) {
          return;
        }
        
        currentIncluded.splice(index, 1);
        this.$store.commit('setPublishingContractConditionsSplitsDetailsInclude', currentIncluded);
      }
    },
    removeExcluded(item, index) {
      if (index >= 0) {
        const currentExcluded = [...this.excludedRegions];
        currentExcluded.splice(index, 1);
        this.$store.commit('setPublishingContractConditionsSplitsDetailsExclude', currentExcluded);
      }
    },
    showSelectedRegions() {
      return (
        this.includedRegions.length > 0 ||
        this.excludedRegions.length > 0
      );
    },
    // Cache A-Z person when selected from autocomplete
    cacheSelectedAzPerson(selectedObject) {
      if (selectedObject && selectedObject.alvId) {
        this.$store.commit("setAzPersonInCache", { 
          alvId: selectedObject.alvId, 
          azObject: selectedObject 
        });
      }
    }
  },
  created() {
    this.$store.commit('setSocieties')
    this.$store.dispatch('fetchAzSocietiesWhereDirectMember')
  }
};
</script>

<style scoped>
.calculation-parameters {
  background-color: #f5f6ec; /* Light background */
  padding: 8px 16px 24px 16px; /* Adjust padding */
  margin: 8px 0; /* Spacing around */
  border-radius: 8px; /* Rounded corners */
}

.bulk-entry-field {
  margin-top: 10px;
}

.bulk-entry-field >>> .v-input__control {
  min-height: calc(40px + 5px);
}


</style>